/**
 * PHARMAKON — « Paramètres › Achats et étiquettes › Types de produits » (refonte WP-67, M-23; D-30, A-77,
 * A-109; CP §15.5-1, §15.7, §15.18): the French copy of the section and its drawer, in one place.
 *
 * Every list, bound and rule comes from the shared twin (`@pharmakon/shared/constants/produit-type-champs.js`
 * — the seven types, the fields of each base, the margin rule, the catalogue bounds) or the server's answer
 * (`bornes`, `usage`): nothing business-wise is typed here.
 */
import { CHAMP_TYPE } from "@pharmakon/shared/constants/produit-type-champs.js";
import { fmtInt } from "@/config/locale.config.js";

/** The words of a type-dependent field of the fiche produit (the inherited rules shown in the drawer). */
export const LIBELLES_CHAMPS_TYPE = Object.freeze({
  [CHAMP_TYPE.DCI]: "DCI",
  [CHAMP_TYPE.DOSAGE]: "Dosage",
  [CHAMP_TYPE.CONTENANCE]: "Contenance",
  [CHAMP_TYPE.FORME]: "Forme",
  [CHAMP_TYPE.LABORATOIRE]: "Laboratoire",
  [CHAMP_TYPE.MARQUE]: "Marque",
  [CHAMP_TYPE.DESCRIPTION]: "Description",
  [CHAMP_TYPE.NUM_ENR]: "N° ENR (CNAS)",
  [CHAMP_TYPE.REGLEMENTATION]: "Réglementation (remboursement, liste, psychotrope)",
  [CHAMP_TYPE.DERMO]: "Dermo-Conseiller",
});

const pluriel = (n, un, plusieurs) => `${fmtInt(n)} ${n > 1 ? plusieurs : un}`;

/** What uses a type, for the grid (« 3 produits · 1 facture »), from the server's `usage`. */
function texteUsage(u) {
  if (!u || u.total === 0) return "Aucun produit ni document";
  const parts = [];
  if (u.produits) parts.push(pluriel(u.produits, "produit", "produits"));
  if (u.factures) parts.push(pluriel(u.factures, "facture", "factures"));
  if (u.bons_commande) parts.push(pluriel(u.bons_commande, "bon de commande", "bons de commande"));
  return parts.join(" · ");
}
/** Same, inside a sentence (« 3 produits, 1 facture »). */
const usageDansPhrase = (u) => texteUsage(u).toLowerCase().replace(/ · /g, ", ");

export const TYPES_PRODUIT_MESSAGES = Object.freeze({
  aide:
    "Les types prédéfinis restent toujours proposés. Ajoutez les vôtres (par exemple « Lait ») : chacun suit les règles de son type de base — champs de la fiche produit, date de péremption, laboratoire ou marque.",
  recherche: "Rechercher un type…",
  afficherInactifs: "Afficher les types désactivés",
  nouveau: "Nouveau type",
  grille: "Types de produits de l'officine",
  colonnes: Object.freeze({
    type: "Type",
    marge: "Marge par défaut",
    usage: "Utilisé par",
    etat: "État",
  }),
  baseDe: (base) => `Type de base : ${base}`,
  predefini: "Prédéfini",
  actif: "Actif",
  inactif: "Désactivé",
  sansMarge: "—",
  margeReglementee: "PPA réglementé",
  aucun: "Aucun type ne correspond à la recherche.",
  aucunAide: "Effacez la recherche pour revoir tous les types.",
  compte: (n, ajoutes) => `${pluriel(n, "type", "types")} affiché${n > 1 ? "s" : ""} — dont ${pluriel(ajoutes, "ajouté par l'officine", "ajoutés par l'officine")}.`,
  usage: texteUsage,
  actions: Object.freeze({
    modifier: "Modifier…",
    desactiver: "Désactiver…",
    reactiver: "Réactiver",
    supprimer: "Supprimer…",
    monter: "Monter",
    descendre: "Descendre",
  }),
  raisons: Object.freeze({
    predefiniEtat: "Type prédéfini : il reste toujours proposé.",
    predefiniSupprime: "Type prédéfini : il ne se supprime pas.",
    utilise: (u) => `Utilisé par ${usageDansPhrase(u)} : désactivez-le plutôt.`,
    premier: "Déjà en tête de la liste.",
    dernier: "Déjà en fin de liste.",
    lectureSeule: "Lecture seule : seul le propriétaire modifie les types.",
    enCours: "Un enregistrement est en cours.",
  }),
  desactiver: Object.freeze({
    titre: (l) => `Désactiver le type « ${l} » ?`,
    message: (u) =>
      u?.total
        ? `Il ne sera plus proposé pour un nouveau produit ou une nouvelle facture. Ce qui le porte déjà le garde (${usageDansPhrase(u)}).`
        : "Il ne sera plus proposé pour un nouveau produit ou une nouvelle facture. Vous pourrez le réactiver.",
    confirmer: "Désactiver",
  }),
  reactiver: Object.freeze({
    titre: (l) => `Réactiver le type « ${l} » ?`,
    message: "Il sera de nouveau proposé dans la saisie des produits et des achats.",
    confirmer: "Réactiver",
  }),
  supprimer: Object.freeze({
    titre: (l) => `Supprimer le type « ${l} » ?`,
    message: "Aucun produit ni document ne le porte : il est supprimé définitivement.",
    confirmer: "Supprimer",
  }),
  annuler: "Annuler",
  cree: (l) => `Type « ${l} » créé.`,
  enregistre: (l) => `Type « ${l} » enregistré.`,
  desactive: (l) => `Type « ${l} » désactivé.`,
  reactive: (l) => `Type « ${l} » réactivé.`,
  supprime: (l) => `Type « ${l} » supprimé.`,
  deplace: (l) => `« ${l} » déplacé.`,
  recharge: "La liste a changé entre-temps : elle a été rechargée.",
  erreurChargement: "Les types de produits n'ont pas pu être chargés.",
  drawer: Object.freeze({
    titreNouveau: "Nouveau type de produit",
    titreModifier: (l) => `Modifier le type « ${l} »`,
    libelle: "Libellé",
    libelleAide: (max) => `Le nom affiché dans les listes et les filtres (${max} caractères au plus).`,
    libellePredefini: "Le libellé d'un type prédéfini ne se modifie pas.",
    base: "Type de base",
    baseAide: "Le type ajouté suit les règles de ce type prédéfini.",
    basePlaceholder: "Choisir le type de base…",
    baseUtilisee: (u) => `Utilisé par ${usageDansPhrase(u)} : le type de base ne se modifie plus.`,
    basePredefini: "Un type prédéfini est son propre type de base.",
    marge: "Marge par défaut",
    margeAide: "Proposée sur les lignes d'achat de ce type pour calculer le prix de vente (laisser vide : aucune).",
    margeSansObjet: "Type de base Médicament : le prix de vente est le PPA réglementé, sans marge à saisir.",
    regles: (base) => `Règles reprises de « ${base} »`,
    reglesChamps: "Champs de la fiche produit",
    reglesPeremption: "Date de péremption",
    reglesProducteur: "Producteur",
    peremption: Object.freeze({ REQUISE: "obligatoire", OPTIONNELLE: "facultative", MASQUEE: "sans objet" }),
    laboratoire: "laboratoire",
    marque: "marque",
    libelleRequis: (min) => `Saisissez le libellé du type (au moins ${min} caractères).`,
    libelleTropLong: (max) => `Libellé : ${max} caractères au plus.`,
    baseRequise: "Choisissez le type de base.",
    margeBornes: (min, max) => `Marge : entre ${min} et ${max} %.`,
    creer: "Créer le type",
    enregistrer: "Enregistrer",
    annuler: "Annuler",
  }),
});

/** Refonte WP-68 — the words of the catalogue's consumers (pickers and filters of the other modules). */
export const TYPES_CATALOGUE_MESSAGES = Object.freeze({
  /** a key no list names (never expected: the server answers with every type the rows name) */
  typeInconnu: "Type de l'officine",
  /** a deactivated type kept on what it names, or offered in a filter */
  desactive: (libelle) => `${libelle} (désactivé)`,
  /** the empty option of a single-type filter */
  tousLesTypes: "Tous les types",
});
